import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as ChevronSolidIcon } from "assets/images/icons/icn_chevron_solid.svg";
import { ReactComponent as DeleteIcon } from "assets/images/icons/icn_table_cell_Unverified.svg";
import btn_add from "assets/images/icons/btn_add.svg";

import React, { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Paper,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { cloneDeep } from "lodash";

import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import { defaultUserAccountItem } from "../util/const";

const getDefaultItem = () => ({
  ...cloneDeep(defaultUserAccountItem),
  open: true,
});

const AddUserAccountModal = ({
  open,
  onClose,
  onApply,
  form,
}) => {
  const theme = useTheme();
  const [items, setItems] = useState([getDefaultItem()]);

  const closeModal = () => {
    setItems([getDefaultItem()]);
    onClose();
  };

  const toggleOpenList = (index) => {
    setItems((prev) => {
      const next = cloneDeep(prev);
      next[index].open = !next[index].open;
      return next;
    });
  };

  const updateItem = (index, key, value) => {
    setItems((prev) => {
      const next = cloneDeep(prev);
      next[index] = {
        ...next[index],
        [key]: value,
      };
      return next;
    });
  };

  const addItem = () => {
    setItems((prev) => [...prev, getDefaultItem()]);
  };

  const deleteItem = (index) => {
    setItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const isFormValid = () => {
    return items.length > 0 && items.every((item) => item.username.trim() && item.password.trim());
  };

  const apply = () => {
    if (!isFormValid()) return;

    onApply(items.map((item) => ({
      username: item.username,
      password: item.password,
      description: item.description,
    })));
    closeModal();
  };

  if (!open) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            paddingLeft: "20px",
          }}
        >
          Add User Account
        </Typography>
        <Button
          onClick={closeModal}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": { backgroundColor: theme.palette.primary.transparent },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          "&.MuiPaper-root": {
            boxShadow: "none",
            border: "none",
          },
        }}
        style={{
          maxHeight: "320px",
          marginLeft: "20px",
          width: "calc(100% - 40px)",
          overflowY: items.length > 2 ? "auto" : "hidden",
        }}
      >
        {items.map((item, index) => {
          return (
            <React.Fragment key={index}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingTop: "20px",
                  cursor: "pointer",
                  height: "40px",
                  gap: "8px",
                }}
                onClick={() => toggleOpenList(index)}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      transform: item.open ? "rotate(90deg)" : "rotate(0deg)",
                    }}
                  >
                    <ChevronSolidIcon />
                  </Box>
                  <Typography
                    sx={{
                      color: theme.palette.primary.greyPrimary,
                      fontSize: theme.typography.customFontSize[2],
                      fontWeight: theme.typography.customFontWeight[1],
                    }}
                  >
                    User Account({index + 1})
                  </Typography>
                </Box>
                {index === 0 ? null : (
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteItem(index);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      padding: "8px",
                    }}
                  >
                    <DeleteIcon />
                  </Box>
                )}
              </Box>
              <Collapse in={item.open} timeout="auto" unmountOnExit>
                <Box sx={{ ...form.getRowStyle() }}>
                  <Box sx={form.getAddItemTopContainerStyle()}>
                    <Box
                      sx={{
                        ...form.getLabelStyle(),
                        marginLeft: "12px",
                      }}
                    >
                      Username
                    </Box>
                    <Box style={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                      <TextField
                        placeholder=""
                        sx={form.getTextfieldStyle(true)}
                        value={item.username}
                        onChange={(e) => updateItem(index, "username", e.target.value)}
                      />
                    </Box>
                    <Box style={{ textAlign: "center" }}>
                      <Typography
                        sx={{
                          color: theme.palette.primary.greyPrimary,
                          fontSize: theme.typography.customFontSize[2],
                        }}
                      >
                        Password
                      </Typography>
                    </Box>
                    <Box style={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                      <TextField
                        placeholder=""
                        sx={form.getTextfieldStyle(true)}
                        value={item.password}
                        onChange={(e) => updateItem(index, "password", e.target.value)}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ ...form.getRowStyle(), marginBottom: "10px" }}>
                  <Box sx={form.getAddItemTopContainerStyle()}>
                    <Box
                      sx={{
                        ...form.getLabelStyle(),
                        marginLeft: "12px",
                      }}
                    >
                      Description
                    </Box>
                    <Box style={{ flexGrow: 1 }}>
                      <TextField
                        placeholder=""
                        sx={form.getTextfieldStyle(true)}
                        value={item.description}
                        onChange={(e) => updateItem(index, "description", e.target.value)}
                      />
                    </Box>
                    <Box style={{ textAlign: "center" }}>
                      <Typography
                        sx={{
                          color: theme.palette.primary.greyPrimary,
                          fontSize: theme.typography.customFontSize[2],
                        }}
                      >
                        (Optional)
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Collapse>
            </React.Fragment>
          );
        })}
      </TableContainer>

      <Box display={"flex"} justifyContent="flex-end" marginTop="20px">
        <Box style={form.getButtonContainerStyle()} onClick={addItem}>
          <Box style={form.getAddButtonStyle()}>
            <img src={btn_add} alt="Add" />
            <span>Add New User Account</span>
          </Box>
        </Box>
      </Box>

      <Box sx={{ margin: "24px 0 16px auto", display: "flex", justifyContent: "center" }}>
        <OutlineButton onClick={closeModal} sx={{ marginRight: "20px" }} width="100px" height="32px">
          Cancel
        </OutlineButton>
        <SolidButton onClick={apply} width="100px" height="32px" disabled={!isFormValid()}>
          Apply
        </SolidButton>
      </Box>
    </Box>
  );
};

export default AddUserAccountModal;
